import { useState } from 'react'
import { Input, Spinner, Stack, Text, XStack, YStack } from 'tamagui'

import { useApp } from '../../src/i18n'
import { Page } from '../../src/ui/Screen'
import { Card, PressCard, SectionCard, Button } from '../../src/ui/primitives'
import { Enter, FadeIn } from '../../src/ui/motion'
import { Icon } from '../../src/ui/Icon'
import { useKbBooks, useKbBook, useKbPage, useKbConfig, useKbSearch } from '../../src/features/kb/useKb'
import { KbHtml } from '../../src/features/kb/KbHtml'
import { KbEmbed } from '../../src/features/kb/KbEmbed'
import type { KbPageRef, KbSearchHit } from '../../src/lib/types'

/**
 * Гибрид: ЧТЕНИЕ — нативно (этот экран, вариант C); АВТОРИНГ (создать/править/схемы/
 * комментарии/права) — в самом BookStack под собой (SSO → авторство+права).
 * Вариант A: авторинг открывается ВСТРОЕННО (iframe на web, KbEmbed) — kb.asgroup.az
 * и портал один сайт asgroup.az, поэтому cookie/SSO работают в iframe.
 */
type Embed = { url: string; title: string }

/** Кнопка авторинга → открывает встроенный вид BookStack (вариант A). */
function BsLink({
  label,
  icon,
  url,
  title,
  onAuthor,
}: {
  label: string
  icon: string
  url?: string
  title: string
  onAuthor: (e: Embed) => void
}) {
  return (
    <Button
      icon={<Icon name={icon} size={15} color="$color" />}
      onPress={() => url && onAuthor({ url, title })}
    >
      {label}
    </Button>
  )
}

function BackBtn({ onBack }: { onBack: () => void }) {
  const { l } = useApp()
  return (
    <Button icon={<Icon name="arrow-left" size={16} color="$color" />} onPress={onBack}>
      {l('kb.back')}
    </Button>
  )
}

function Loading() {
  return (
    <Card padding={40} alignItems="center" justifyContent="center" minHeight={200}>
      <Spinner size="large" color="$primary" />
    </Card>
  )
}

function ErrorCard({ err }: { err: unknown }) {
  const { l, t } = useApp()
  const msg = err instanceof Error ? err.message : String(err)
  const notConfigured = /503/.test(msg)
  return (
    <Card padding={28} gap={10} alignItems="center" minHeight={180} justifyContent="center">
      <Icon name="triangle-alert" size={26} color="$primary" />
      <Text fontSize={15} fontWeight="700" color="$color" textAlign="center">
        {notConfigured ? l('kb.notConfigured') : t({ ru: 'Ошибка загрузки', en: 'Failed to load', az: 'Yükləmə xətası' })}
      </Text>
      <Text fontSize={13} color="$muted" textAlign="center" maxWidth={460}>
        {notConfigured ? l('kb.notConfiguredHint') : msg}
      </Text>
    </Card>
  )
}

export default function Kb() {
  const { l } = useApp()
  const [bookId, setBookId] = useState<number | null>(null)
  const [page, setPage] = useState<KbPageRef | null>(null)
  const [embed, setEmbed] = useState<Embed | null>(null)

  // ── Встроенный BookStack (авторинг, вариант A) — поверх всего ────────
  if (embed != null) {
    return <KbEmbed url={embed.url} title={embed.title} backLabel={l('kb.back')} onBack={() => setEmbed(null)} />
  }

  // ── Страница (читалка) ──────────────────────────────────────────────
  if (page != null) {
    return <PageView page={page} onBack={() => setPage(null)} onAuthor={setEmbed} />
  }

  // ── Книга (структура) ───────────────────────────────────────────────
  if (bookId != null) {
    return <BookView bookId={bookId} onBack={() => setBookId(null)} onOpenPage={setPage} onAuthor={setEmbed} />
  }

  // ── Список книг + поиск ─────────────────────────────────────────────
  return <BooksView onOpenBook={setBookId} onOpenPage={setPage} onAuthor={setEmbed} />
}

/** Иконка результата поиска по типу сущности BookStack. */
function hitIcon(type: string): string {
  if (type === 'page') return 'file-text'
  if (type === 'chapter') return 'list'
  return 'book-open' // book / bookshelf
}

/** Результаты поиска: страница → нативная читалка; глава/книга → встроенный BookStack. */
function SearchResults({
  hits,
  loading,
  onOpenPage,
  onAuthor,
}: {
  hits: KbSearchHit[] | undefined
  loading: boolean
  onOpenPage: (p: KbPageRef) => void
  onAuthor: (e: Embed) => void
}) {
  const { l } = useApp()
  if (loading) return <Loading />
  if (!hits?.length)
    return (
      <Card padding={32} alignItems="center">
        <Text color="$muted">{l('kb.nothing')}</Text>
      </Card>
    )
  return (
    <YStack gap={10}>
      {hits.map((h, i) => (
        <Enter key={`${h.type}-${h.id}`} index={Math.min(i, 11)}>
        <PressCard
          onPress={() =>
            h.type === 'page'
              ? onOpenPage({ id: h.id, name: h.name, slug: '', url: h.url })
              : onAuthor({ url: h.url, title: h.name })
          }
          padding={14}
          flexDirection="row"
          alignItems="center"
          gap={12}
        >
          <Icon name={hitIcon(h.type)} size={18} color="$primary" />
          <YStack flex={1} gap={2}>
            <Text fontSize={14} fontWeight="700" color="$color">
              {h.name}
            </Text>
            {!!h.preview && (
              <Text fontSize={12} color="$muted" numberOfLines={2}>
                {h.preview}
              </Text>
            )}
          </YStack>
          <Icon name="chevron-right" size={18} color="$muted" />
        </PressCard>
        </Enter>
      ))}
    </YStack>
  )
}

function BooksView({
  onOpenBook,
  onOpenPage,
  onAuthor,
}: {
  onOpenBook: (id: number) => void
  onOpenPage: (p: KbPageRef) => void
  onAuthor: (e: Embed) => void
}) {
  const { l } = useApp()
  const { data, isLoading, error } = useKbBooks()
  const { data: cfg } = useKbConfig()
  const [q, setQ] = useState('')
  const searching = q.trim().length >= 2
  const { data: hits, isFetching } = useKbSearch(q)
  return (
    <Page
      title={l('kb.title')}
      subtitle={l('kb.subtitle')}
      action={<BsLink label={l('kb.openBs')} icon="external-link" url={cfg?.baseUrl} title="BookStack" onAuthor={onAuthor} />}
    >
      <FadeIn>
        <XStack
          alignItems="center"
          gap={8}
          backgroundColor="$background"
          borderWidth={1}
          borderColor="$borderColor"
          borderRadius={7}
          paddingHorizontal={12}
        >
          <Icon name="search" size={16} color="$muted" />
          <Input
            flex={1}
            unstyled
            height={40}
            fontSize={14}
            color="$color"
            placeholder={l('kb.searchPh')}
            placeholderTextColor="$muted"
            value={q}
            onChangeText={setQ}
          />
          {q.length > 0 && (
            <Stack cursor="pointer" padding={4} onPress={() => setQ('')}>
              <Icon name="x" size={16} color="$muted" />
            </Stack>
          )}
        </XStack>
      </FadeIn>

      {searching ? (
        <SearchResults hits={hits} loading={isFetching} onOpenPage={onOpenPage} onAuthor={onAuthor} />
      ) : isLoading ? (
        <Loading />
      ) : error ? (
        <ErrorCard err={error} />
      ) : !data?.length ? (
        <Card padding={40} alignItems="center">
          <Text color="$muted">{l('kb.empty')}</Text>
        </Card>
      ) : (
        <YStack gap={12}>
          {data.map((b, i) => (
            <Enter key={b.id} index={Math.min(i, 11)}>
            <PressCard
              onPress={() => onOpenBook(b.id)}
              padding={16}
              flexDirection="row"
              alignItems="center"
              gap={14}
            >
              <YStack
                width={42}
                height={42}
                borderRadius={7}
                backgroundColor="$primaryBg"
                alignItems="center"
                justifyContent="center"
              >
                <Icon name="book-open" size={22} color="$primary" />
              </YStack>
              <YStack flex={1} gap={2}>
                <Text fontSize={15} fontWeight="700" color="$color">
                  {b.name}
                </Text>
                {!!b.description && (
                  <Text fontSize={13} color="$muted" numberOfLines={2}>
                    {b.description}
                  </Text>
                )}
              </YStack>
              <Icon name="chevron-right" size={20} color="$muted" />
            </PressCard>
            </Enter>
          ))}
        </YStack>
      )}
    </Page>
  )
}

function BookView({
  bookId,
  onBack,
  onOpenPage,
  onAuthor,
}: {
  bookId: number
  onBack: () => void
  onOpenPage: (p: KbPageRef) => void
  onAuthor: (e: Embed) => void
}) {
  const { l } = useApp()
  const { data, isLoading, error } = useKbBook(bookId)
  return (
    <Page
      title={data?.name ?? l('kb.title')}
      subtitle={data?.description || undefined}
      action={
        <XStack gap={8} flexWrap="wrap">
          {data && (
            <BsLink label={l('kb.addPage')} icon="plus" url={data.createPageUrl} title={data.name} onAuthor={onAuthor} />
          )}
          <BackBtn onBack={onBack} />
        </XStack>
      }
    >
      {isLoading ? (
        <Loading />
      ) : error ? (
        <ErrorCard err={error} />
      ) : (
        <YStack gap={14}>
          {data?.sections.map((s, si) => (
            <Enter key={s.id ?? 'loose'} index={Math.min(si, 11)}>
            <SectionCard title={s.name ?? l('kb.loose')} pad={0}>
              <YStack>
                {s.pages.map((p, i) => (
                  <PressCard
                    key={p.id}
                    onPress={() => onOpenPage(p)}
                    padding={13}
                    flexDirection="row"
                    alignItems="center"
                    gap={10}
                    borderRadius={0}
                    borderTopWidth={i === 0 ? 0 : 1}
                    borderColor="$borderSecondary"
                  >
                    <Icon name="file-text" size={17} color="$primary" />
                    <Text flex={1} fontSize={14} color="$color">
                      {p.name}
                    </Text>
                    <Icon name="chevron-right" size={18} color="$muted" />
                  </PressCard>
                ))}
              </YStack>
            </SectionCard>
            </Enter>
          ))}
        </YStack>
      )}
    </Page>
  )
}

function PageView({
  page,
  onBack,
  onAuthor,
}: {
  page: KbPageRef
  onBack: () => void
  onAuthor: (e: Embed) => void
}) {
  const { l } = useApp()
  const { data, isLoading, error } = useKbPage(page.id)
  return (
    <Page
      title={data?.name ?? page.name}
      action={
        <XStack gap={8} flexWrap="wrap">
          <BsLink label={l('kb.edit')} icon="pencil" url={`${page.url}/edit`} title={data?.name ?? page.name} onAuthor={onAuthor} />
          <BackBtn onBack={onBack} />
        </XStack>
      }
    >
      {isLoading ? (
        <Loading />
      ) : error ? (
        <ErrorCard err={error} />
      ) : (
        <Card padding={24}>
          <KbHtml html={data?.html ?? ''} />
        </Card>
      )}
    </Page>
  )
}
